import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, MessageSquare, Send, RefreshCw, Bell, BellOff, Copy, ExternalLink, Clock, FlaskConical, X, Archive, RotateCcw, Monitor, Link2, Info, Wifi, WifiOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Função para normalizar texto (remover acentos)
const normalizeText = (text) => {
  if (!text) return '';
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};

// Small pill showing WhatsApp connection status
const WhatsappStatusPill = ({ status, compact = false }) => {
  const config = {
    connected: {
      label: 'WhatsApp Conectado',
      short: 'Conectado',
      Icon: Wifi,
      bg: 'bg-emerald-500/15',
      text: 'text-emerald-400',
      border: 'border-emerald-500/30',
    },
    qr_code: {
      label: 'Aguardando QR Code',
      short: 'QR pendente',
      Icon: WifiOff,
      bg: 'bg-amber-500/15',
      text: 'text-amber-400',
      border: 'border-amber-500/30',
    },
    connecting: {
      label: 'Conectando…',
      short: 'Conectando',
      Icon: Loader2,
      bg: 'bg-sky-500/15',
      text: 'text-sky-400',
      border: 'border-sky-500/30',
      spin: true,
    },
    disconnected: {
      label: 'WhatsApp Desconectado',
      short: 'Desconectado',
      Icon: WifiOff,
      bg: 'bg-red-500/15',
      text: 'text-red-400',
      border: 'border-red-500/30',
    },
    checking: {
      label: 'Verificando…',
      short: 'Verificando',
      Icon: Loader2,
      bg: 'bg-slate-500/15',
      text: 'text-slate-400',
      border: 'border-slate-500/30',
      spin: true,
    },
  };
  const c = config[status] || {
    label: 'Status desconhecido',
    short: 'Desconhecido',
    Icon: WifiOff,
    bg: 'bg-slate-500/15',
    text: 'text-slate-400',
    border: 'border-slate-500/30',
  };
  const Icon = c.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
      <Icon className={`w-3 h-3 ${c.spin ? 'animate-spin' : ''}`} strokeWidth={2.5} />
      {compact ? c.short : c.label}
    </span>
  );
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [customTrialTime, setCustomTrialTime] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [renewalType, setRenewalType] = useState('same_day_next_month');
  const [customDate, setCustomDate] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedClients, setArchivedClients] = useState([]);
  const [linkHistoryOpen, setLinkHistoryOpen] = useState(false);
  const [linkHistoryData, setLinkHistoryData] = useState(null);
  const [linkHistoryLoading, setLinkHistoryLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    status: 'ativo',
    monthly_amount: 0,
    product_id: '',
    group_id: '',
    link_id: '',
    link_ids: [],
    payment_date: '',
    notes: '',
    screen_name: 'TV 1',
    mac_email: '',
    otp_key: '',
    upload_link: '',
    screens: [], // Array de telas adicionais
    notifications_enabled: true
  });
  const [userLinks, setUserLinks] = useState([]);
  const [waStatus, setWaStatus] = useState({ status: 'checking', phone: null });
  const [waReconnecting, setWaReconnecting] = useState(false);

  const fetchWhatsappStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || {};
      // Normalize: backend may return {status, connected, phone_number} or {status, phone}
      const rawStatus = (data.status || '').toLowerCase();
      let normalized = 'unknown';
      if (data.connected === true || ['working', 'connected', 'open'].includes(rawStatus)) {
        normalized = 'connected';
      } else if (['scan_qr_code', 'qr_code', 'qrcode', 'qr'].includes(rawStatus)) {
        normalized = 'qr_code';
      } else if (['starting', 'connecting'].includes(rawStatus)) {
        normalized = 'connecting';
      } else if (['stopped', 'disconnected', 'logged_out', 'replaced_by_another_session', 'too_many_reconnect_attempts', 'close'].includes(rawStatus)) {
        normalized = 'disconnected';
      } else {
        normalized = rawStatus || 'unknown';
      }
      setWaStatus({
        status: normalized,
        phone: data.phone_number || data.phone || data.engine?.phone_number || null,
      });
    } catch (error) {
      setWaStatus({ status: 'unknown', phone: null });
    }
  };

  const forceReconnectWhatsapp = async () => {
    setWaReconnecting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/reconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = response.data?.result || {};
      if (result.success || (result.status || '').toLowerCase() === 'connected') {
        toast.success('WhatsApp reconectado!');
      } else {
        toast.warning(`Status: ${result.status || 'não conectado'}. Talvez seja preciso escanear o QR novamente.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao reconectar');
    } finally {
      await fetchWhatsappStatus();
      setWaReconnecting(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchProducts();
    fetchTemplates();
    fetchGroups();
    fetchUserLinks();
    fetchWhatsappStatus();
    // Refresh WhatsApp status every 30s so the badge on each card stays fresh
    const interval = setInterval(fetchWhatsappStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showArchived) {
      fetchArchivedClients();
    }
  }, [showArchived]);

  const fetchUserLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLinks(response.data);
    } catch (error) {
      console.error('Erro ao carregar links');
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const openLinkHistory = async (client) => {
    setSelectedClient(client);
    setLinkHistoryOpen(true);
    setLinkHistoryData(null);
    setLinkHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${client.id}/link-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkHistoryData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico de vínculos');
    } finally {
      setLinkHistoryLoading(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
             d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  const formatDays = (days) => {
    if (days === null || days === undefined) return '—';
    const d = Number(days);
    if (d < 1) {
      const hours = Math.round(d * 24);
      return `${hours} h`;
    }
    if (d < 30) return `${Math.round(d)} dia${Math.round(d) !== 1 ? 's' : ''}`;
    if (d < 365) {
      const months = (d / 30).toFixed(1);
      return `${months} meses`;
    }
    const years = (d / 365).toFixed(1);
    return `${years} ano${years !== '1.0' ? 's' : ''}`;
  };

  const fetchArchivedClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes arquivados');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Erro ao carregar produtos');
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.filter(t => t.active));
    } catch (error) {
      console.error('Erro ao carregar templates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    // Set payment_date based on product if not manually set
    let submitData = { ...formData };
    
    // Clean empty product_id
    if (!submitData.product_id) {
      delete submitData.product_id;
    }
    
    // Calculate payment date if product selected and no manual date
    if (submitData.product_id && !submitData.payment_date) {
      const today = new Date();
      const product = products.find(p => p.id === submitData.product_id);
      if (product) {
        const plan_days = {
          monthly: 30,
          bimonthly: 60,
          quarterly: 90,
          semiannual: 180,
          annual: 365
        };
        const days = plan_days[product.plan_type] || 30;
        const futureDate = new Date(today);
        futureDate.setDate(today.getDate() + days);
        submitData.payment_date = futureDate.toISOString();
      }
    }

    // Convert date string to ISO if manually set
    if (submitData.payment_date && typeof submitData.payment_date === 'string' && submitData.payment_date.length === 10) {
      submitData.payment_date = new Date(submitData.payment_date + 'T12:00:00Z').toISOString();
    }

    console.log('Submitting client data:', submitData);

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Cliente atualizado!');
      } else {
        const response = await axios.post(`${API}/clients`, submitData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        console.log('Client created:', response.data);
        toast.success('Cliente criado com sucesso!');
      }
      await fetchClients();
      setOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving client:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao salvar cliente';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cliente excluído!');
      fetchClients();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const toggleNotifications = async (clientId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/clients/${clientId}/toggle-notifications?enabled=${!currentStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Notificações ${!currentStatus ? 'ativadas' : 'desativadas'}`);
      fetchClients();
    } catch (error) {
      toast.error('Erro ao alterar notificações');
    }
  };

  const openWhatsApp = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const openRenewDialog = (client) => {
    setSelectedClient(client);
    setRenewalType('same_day_next_month');
    setCustomDate('');
    setRenewDialogOpen(true);
  };

  const renewClient = async () => {
    if (!selectedClient) return;

    try {
      const token = localStorage.getItem('token');
      
      const requestBody = {
        renewal_type: renewalType,
        custom_date: renewalType === 'custom' ? customDate : null,
      };

      const response = await axios.post(
        `${API}/clients/${selectedClient.id}/renew`,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      toast.success(result.message);
      
      setRenewDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao renovar cliente');
    }
  };

  // Funções para Cliente em Teste
  const openTrialDialog = (client) => {
    setSelectedClient(client);
    setCustomTrialTime('');
    setTrialDialogOpen(true);
  };

  // Converter hh:mm para horas decimais
  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return null;
    
    // Se contém ":", é formato hh:mm
    if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      return hours + (minutes / 60);
    }
    
    // Senão, assume que é apenas minutos se for número pequeno sem separador
    const num = parseFloat(timeStr);
    if (isNaN(num)) return null;
    
    // Se digitou só número, assumir que são horas
    return num;
  };

  // Formatar para exibição
  const formatHoursToTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const startTrial = async (hours) => {
    if (!selectedClient) return;
    
    const hoursValue = typeof hours === 'number' ? hours : parseTimeToHours(hours);
    
    if (hoursValue === null || hoursValue < (1/60) || hoursValue > 72) {
      toast.error('Duração deve ser entre 00:01 (1 min) e 72:00 (72h)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/clients/${selectedClient.id}/start-trial`,
        { hours: hoursValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setTrialDialogOpen(false);
      setCustomTrialTime('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar teste');
    }
  };

  const startTrialWithTime = () => {
    const hours = parseTimeToHours(customTrialTime);
    if (hours === null) {
      toast.error('Formato inválido. Use hh:mm (ex: 01:30)');
      return;
    }
    startTrial(hours);
  };

  const cancelTrial = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/clients/${clientId}/cancel-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Teste cancelado');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar teste');
    }
  };

  const archiveClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/clients/${clientId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao arquivar cliente');
    }
  };

  const restoreClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/clients/${clientId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchClients();
      fetchArchivedClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar cliente');
    }
  };

  // Calcular tempo restante do teste
  const getTrialTimeRemaining = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diffMs = end - now;
    if (diffMs <= 0) return { expired: true, text: 'Expirado' };
    
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    
    // Formato hh:mm
    const hoursStr = hours.toString().padStart(2, '0');
    const minsStr = mins.toString().padStart(2, '0');
    
    return { expired: false, text: `${hoursStr}:${minsStr} restantes` };
  };

  // Função para copiar para clipboard
  const copyToClipboard = (text, label) => {
    if (!text) {
      toast.error(`${label} não disponível`);
      return;
    }
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copiado!`))
      .catch(() => toast.error('Erro ao copiar'));
  };

  // Função para filtrar e ordenar clientes
  const getFilteredAndSortedClients = () => {
    let filtered = [...clients];

    // Filtrar por busca (nome ou telefone) - ignorando acentos
    if (searchTerm.trim()) {
      const search = normalizeText(searchTerm);
      filtered = filtered.filter(client => 
        normalizeText(client.name).includes(search) ||
        normalizeText(client.phone).includes(search)
      );
    }

    // Filtrar por produto
    if (filterProduct !== 'all') {
      filtered = filtered.filter(client => client.product_id === filterProduct);
    }

    // Filtrar por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(client => client.status === filterStatus);
    }

    // Ordenar
    filtered.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case 'payment_date':
          // Ordenar por vencimento (mais próximo primeiro por padrão)
          valueA = a.payment_date ? new Date(a.payment_date).getTime() : Infinity;
          valueB = b.payment_date ? new Date(b.payment_date).getTime() : Infinity;
          break;
        
        case 'name':
          valueA = a.name?.toLowerCase() || '';
          valueB = b.name?.toLowerCase() || '';
          break;
        
        case 'monthly_amount':
          // Ordenar por valor
          valueA = a.monthly_amount || 0;
          valueB = b.monthly_amount || 0;
          break;
        
        case 'product':
          // Ordenar por nome do produto
          const productA = products.find(p => p.id === a.product_id);
          const productB = products.find(p => p.id === b.product_id);
          valueA = productA?.name?.toLowerCase() || '';
          valueB = productB?.name?.toLowerCase() || '';
          break;
        
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });

    return filtered;
  };

  const filteredClients = getFilteredAndSortedClients();

  // Função para calcular dias até vencimento e retornar badge
  const getDaysUntilDue = (paymentDate) => {
    if (!paymentDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDate = new Date(paymentDate);
    dueDate.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getDueDateBadge = (paymentDate) => {
    const days = getDaysUntilDue(paymentDate);
    
    if (days === null) return null;
    
    if (days < 0) {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-400 border border-red-800">
          ⚠️ Vencido há {Math.abs(days)} dia(s)
        </div>
      );
    } else if (days === 0) {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-900/30 text-orange-400 border border-orange-800">
          🔥 Vence hoje
        </div>
      );
    } else if (days <= 3) {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-800">
          ⏰ Vence em {days} dia(s)
        </div>
      );
    } else if (days <= 7) {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400 border border-blue-800">
          📅 {days} dias
        </div>
      );
    } else {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-800">
          ✓ {days} dias
        </div>
      );
    }
  };

  const openTemplateSender = (client) => {
    setSelectedClient(client);
    setSelectedTemplate('');
    setTemplateDialogOpen(true);
  };

  const sendTemplate = async () => {
    if (!selectedTemplate) {
      toast.error('Selecione um template');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/clients/${selectedClient.id}/send-template?template_id=${selectedTemplate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setTemplateDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      status: 'ativo',
      monthly_amount: 0,
      product_id: '',
      group_id: '',
      link_id: '',
      link_ids: [],
      payment_date: '',
      notes: '',
      screen_name: 'TV 1',
      mac_email: '',
      otp_key: '',
      upload_link: '',
      screens: [],
      notifications_enabled: true
    });
    setEditingClient(null);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phone: client.phone,
      status: client.status,
      monthly_amount: client.monthly_amount,
      product_id: client.product_id || '',
      group_id: client.group_id || '',
      link_id: client.link_id || '',
      link_ids: client.link_ids || [],
      payment_date: client.payment_date ? new Date(client.payment_date).toISOString().split('T')[0] : '',
      notes: client.notes,
      screen_name: client.screen_name || 'TV 1',
      mac_email: client.mac_email || '',
      otp_key: client.otp_key || '',
      upload_link: client.upload_link || '',
      screens: client.screens || [],
      notifications_enabled: client.notifications_enabled !== false
    });
    setOpen(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      ativo: 'inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-600',
      inativo: 'inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600',
      atrasado: 'inline-flex items-center rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-0.5 text-xs font-semibold text-rose-600',
      em_teste: 'inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-600'
    };
    return badges[status] || badges.ativo;
  };

  const getStatusLabel = (status) => {
    const labels = {
      ativo: 'Ativo',
      inativo: 'Inativo',
      atrasado: 'Atrasado',
      em_teste: '🧪 Em Teste'
    };
    return labels[status] || status;
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="clients-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8 h-screen overflow-y-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Clientes</h1>
            <p className="text-sm text-slate-400">Gerencie seus clientes e planos</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-violet-600 hover:bg-violet-700 text-white text-sm"
                data-testid="add-client-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader className="sticky top-0 bg-zinc-900 pb-4 z-10">
                <DialogTitle>{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pb-4" data-testid="client-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nome</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      placeholder="5511999999999"
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger data-testid="client-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                        <SelectItem value="atrasado">Atrasado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="monthly_amount">Mensalidade (R$)</Label>
                    <Input
                      id="monthly_amount"
                      type="number"
                      step="0.01"
                      value={formData.monthly_amount}
                      onChange={(e) => setFormData({...formData, monthly_amount: parseFloat(e.target.value)})}
                      data-testid="client-amount-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Produto/Plano (Opcional)</Label>
                  <Select value={formData.product_id || undefined} onValueChange={(value) => setFormData({...formData, product_id: value})}>
                    <SelectTrigger data-testid="client-product-select">
                      <SelectValue placeholder="Selecione um produto ou deixe em branco" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} - R$ {product.price.toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.product_id && (
                    <p className="text-xs text-slate-400 mt-1">
                      Próximo vencimento será calculado automaticamente
                    </p>
                  )}
                  {formData.product_id && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setFormData({...formData, product_id: ''})}
                      className="mt-2 text-xs"
                    >
                      Limpar seleção
                    </Button>
                  )}
                </div>

                <div>
                  <Label>Grupo (Opcional)</Label>
                  <Select value={formData.group_id || 'none'} onValueChange={(value) => setFormData({...formData, group_id: value === 'none' ? '' : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem grupo</SelectItem>
                      {groups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{backgroundColor: group.color}}></div>
                            {group.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">
                    Cliente receberá apenas templates deste grupo
                  </p>
                </div>

                <div>
                  <Label>Links Vinculados (Opcional)</Label>
                  <div className="mt-2 space-y-2 max-h-32 overflow-y-auto bg-zinc-800 rounded-lg p-2">
                    {userLinks.length === 0 ? (
                      <p className="text-xs text-slate-500 p-2">Nenhum link cadastrado</p>
                    ) : (
                      userLinks.map(link => {
                        const isSelected = formData.link_ids?.includes(link.id) || formData.link_id === link.id;
                        return (
                          <label 
                            key={link.id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              isSelected ? 'bg-emerald-500/20 border border-emerald-500/30' : 'hover:bg-zinc-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                let newLinkIds = [...(formData.link_ids || [])];
                                // Migrar link_id antigo para link_ids se existir
                                if (formData.link_id && !newLinkIds.includes(formData.link_id)) {
                                  newLinkIds.push(formData.link_id);
                                }
                                if (e.target.checked) {
                                  if (!newLinkIds.includes(link.id)) {
                                    newLinkIds.push(link.id);
                                  }
                                } else {
                                  newLinkIds = newLinkIds.filter(id => id !== link.id);
                                }
                                setFormData({...formData, link_ids: newLinkIds, link_id: ''});
                              }}
                              className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-emerald-500 focus:ring-emerald-500"
                            />
                            <div className="flex items-center gap-2 flex-1">
                              {link.icon_url ? (
                                <img src={link.icon_url} alt="" className="w-5 h-5 rounded object-cover" />
                              ) : (
                                <Link2 className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="text-sm text-white">{link.name}</span>
                              {link.credit_cost > 0 && (
                                <span className="text-xs text-emerald-400 ml-auto">R$ {link.credit_cost.toFixed(2)}</span>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Selecione um ou mais links para vincular ao cliente
                  </p>
                </div>

                <div>
                  <Label htmlFor="payment_date">Data de Vencimento (Opcional)</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    data-testid="client-payment-date-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Deixe em branco para usar o prazo do produto
                  </p>
                </div>

                <div>
                  <Label htmlFor="notes">Observações</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full h-20 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    data-testid="client-notes-input"
                  />
                </div>

                {/* Tela 1 - Principal */}
                <div className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Monitor className="w-4 h-4 text-violet-400" />
                    <Input
                      type="text"
                      placeholder="TV 1"
                      value={formData.screen_name || ''}
                      onChange={(e) => setFormData({...formData, screen_name: e.target.value})}
                      className="w-32 h-8 text-sm bg-zinc-700 border-zinc-600"
                    />
                    <span className="text-xs text-slate-400">(Principal)</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="mac_email">MAC/E-mail</Label>
                      <Input
                        id="mac_email"
                        type="text"
                        placeholder="Ex: 00:1A:2B:3C:4D:5E ou email@exemplo.com"
                        value={formData.mac_email}
                        onChange={(e) => setFormData({...formData, mac_email: e.target.value})}
                        data-testid="client-mac-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="otp_key">Chave OTP/Senha</Label>
                      <Input
                        id="otp_key"
                        type="text"
                        placeholder="Ex: JBSWY3DPEHPK3PXP ou senha123"
                        value={formData.otp_key}
                        onChange={(e) => setFormData({...formData, otp_key: e.target.value})}
                        data-testid="client-otp-key-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="upload_link">Link Subir Lista</Label>
                      <Input
                        id="upload_link"
                        type="url"
                        placeholder="https://exemplo.com/upload"
                        value={formData.upload_link}
                        onChange={(e) => setFormData({...formData, upload_link: e.target.value})}
                        data-testid="client-upload-link-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Telas Adicionais */}
                {formData.screens && formData.screens.map((screen, index) => (
                  <div key={index} className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-emerald-400" />
                        <Input
                          type="text"
                          placeholder={`TV ${index + 2}`}
                          value={screen.name || ''}
                          onChange={(e) => {
                            const newScreens = [...formData.screens];
                            newScreens[index] = {...newScreens[index], name: e.target.value};
                            setFormData({...formData, screens: newScreens});
                          }}
                          className="w-32 h-8 text-sm bg-zinc-700 border-zinc-600"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newScreens = formData.screens.filter((_, i) => i !== index);
                          setFormData({...formData, screens: newScreens});
                        }}
                        className="p-1 hover:bg-red-900/30 rounded text-red-400"
                        title="Remover tela"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <Label>MAC/E-mail</Label>
                        <Input
                          type="text"
                          placeholder="Ex: 00:1A:2B:3C:4D:5E ou email@exemplo.com"
                          value={screen.mac_email || ''}
                          onChange={(e) => {
                            const newScreens = [...formData.screens];
                            newScreens[index] = {...newScreens[index], mac_email: e.target.value};
                            setFormData({...formData, screens: newScreens});
                          }}
                        />
                      </div>

                      <div>
                        <Label>Chave OTP/Senha</Label>
                        <Input
                          type="text"
                          placeholder="Ex: JBSWY3DPEHPK3PXP ou senha123"
                          value={screen.otp_key || ''}
                          onChange={(e) => {
                            const newScreens = [...formData.screens];
                            newScreens[index] = {...newScreens[index], otp_key: e.target.value};
                            setFormData({...formData, screens: newScreens});
                          }}
                        />
                      </div>

                      <div>
                        <Label>Link Subir Lista</Label>
                        <Input
                          type="url"
                          placeholder="https://exemplo.com/upload"
                          value={screen.upload_link || ''}
                          onChange={(e) => {
                            const newScreens = [...formData.screens];
                            newScreens[index] = {...newScreens[index], upload_link: e.target.value};
                            setFormData({...formData, screens: newScreens});
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Botão Adicionar Nova Tela */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const screenNumber = (formData.screens?.length || 0) + 2;
                    const newScreen = { name: `TV ${screenNumber}`, mac_email: '', otp_key: '', upload_link: '' };
                    setFormData({...formData, screens: [...(formData.screens || []), newScreen]});
                  }}
                  className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-violet-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Nova Tela
                </Button>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notifications"
                    checked={formData.notifications_enabled}
                    onChange={(e) => setFormData({...formData, notifications_enabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="notifications">Receber notificações automáticas</Label>
                </div>

                <div className="pt-4 sticky bottom-0 bg-zinc-900">
                  <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="client-submit-btn">
                    {editingClient ? 'Atualizar' : 'Criar'} Cliente
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Template Sender Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Enviar Template para {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* WhatsApp status + reconnect button */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700">
                <div className="flex items-center gap-2">
                  <WhatsappStatusPill status={waStatus.status} />
                  {waStatus.phone && (
                    <span className="text-xs text-slate-400">({waStatus.phone})</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={forceReconnectWhatsapp}
                  disabled={waReconnecting}
                  className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                  data-testid="btn-force-reconnect-whatsapp"
                >
                  {waReconnecting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reconectando…</>
                  ) : (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reconectar</>
                  )}
                </Button>
              </div>

              {waStatus.status !== 'connected' && (
                <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
                  ⚠️ WhatsApp não está conectado. Clique em "Reconectar" antes de enviar.
                </div>
              )}

              <div>
                <Label>Selecione o Template</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha um template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates
                      .filter(template => {
                        // Mostrar apenas templates do mesmo grupo do cliente ou templates sem grupo
                        const templateGroup = template.group_id;
                        const clientGroup = selectedClient?.group_id;
                        
                        if (!templateGroup) return true; // Template sem grupo específico
                        return templateGroup === clientGroup; // Template do mesmo grupo
                      })
                      .length === 0 ? (
                        <SelectItem value="none" disabled>
                          Nenhum template disponível para este grupo
                        </SelectItem>
                      ) : (
                        templates
                          .filter(template => {
                            const templateGroup = template.group_id;
                            const clientGroup = selectedClient?.group_id;
                            if (!templateGroup) return true;
                            return templateGroup === clientGroup;
                          })
                          .map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))
                      )}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={sendTemplate} className="w-full bg-emerald-600 hover:bg-emerald-700">
                <Send className="w-4 h-4 mr-2" />
                Enviar Mensagem
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Renewal Dialog */}
        <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Renovar Plano - {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* WhatsApp status + reconnect (renovação também dispara template automático) */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700">
                <div className="flex items-center gap-2">
                  <WhatsappStatusPill status={waStatus.status} />
                  {waStatus.phone && (
                    <span className="text-xs text-slate-400">({waStatus.phone})</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={forceReconnectWhatsapp}
                  disabled={waReconnecting}
                  className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                  data-testid="btn-force-reconnect-whatsapp-renew"
                >
                  {waReconnecting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reconectando…</>
                  ) : (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reconectar</>
                  )}
                </Button>
              </div>

              {waStatus.status !== 'connected' && (
                <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
                  ⚠️ WhatsApp não está conectado. A renovação disparará o template de renovação — clique em "Reconectar" primeiro.
                </div>
              )}

              <div className="text-sm text-slate-400">
                <p>Cliente: <span className="text-white font-medium">{selectedClient?.name}</span></p>
                <p>Vencimento atual: <span className="text-white font-medium">
                  {selectedClient?.payment_date ? new Date(selectedClient.payment_date).toLocaleDateString('pt-BR') : 'Não definido'}
                </span></p>
              </div>

              <div>
                <Label>Tipo de Renovação</Label>
                <Select value={renewalType} onValueChange={setRenewalType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Mesmo dia do próximo mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_day_next_month">
                      Mesmo dia do próximo mês
                    </SelectItem>
                    <SelectItem value="custom">
                      Data personalizada
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {renewalType === 'same_day_next_month' && (
                <div className="bg-green-900/20 border border-green-800 rounded p-3 text-sm">
                  <p className="text-green-300">
                    ℹ️ O vencimento será renovado para o mesmo dia do próximo mês.
                    {selectedClient?.payment_date && (
                      <span className="block mt-1">
                        Exemplo: {new Date(new Date(selectedClient.payment_date).setMonth(new Date(selectedClient.payment_date).getMonth() + 1)).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {renewalType === 'custom' && (
                <div>
                  <Label htmlFor="custom_date">Data Personalizada</Label>
                  <Input
                    id="custom_date"
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Escolha a nova data de vencimento
                  </p>
                </div>
              )}

              <div className="bg-yellow-900/20 border border-yellow-800 rounded p-3 text-sm">
                <p className="text-yellow-300">
                  Uma mensagem de confirmação será enviada automaticamente via WhatsApp após a renovação.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => setRenewDialogOpen(false)} 
                  variant="outline"
                  className="flex-1 border-zinc-700 hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={renewClient} 
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  disabled={renewalType === 'custom' && !customDate}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Confirmar Renovação
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Trial Dialog */}
        <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-amber-500" />
                Cliente em Teste - {selectedClient?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-400 text-sm">
                Selecione a duração do período de teste. Uma notificação será enviada automaticamente 15 minutos antes do término.
              </p>
              
              {/* Opções rápidas */}
              <div className="grid grid-cols-4 gap-2">
                <Button
                  onClick={() => startTrial(2)}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex flex-col items-center py-4"
                >
                  <Clock className="w-5 h-5 mb-1" />
                  <span className="font-bold">2h</span>
                </Button>
                <Button
                  onClick={() => startTrial(4)}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex flex-col items-center py-4"
                >
                  <Clock className="w-5 h-5 mb-1" />
                  <span className="font-bold">4h</span>
                </Button>
                <Button
                  onClick={() => startTrial(6)}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex flex-col items-center py-4"
                >
                  <Clock className="w-5 h-5 mb-1" />
                  <span className="font-bold">6h</span>
                </Button>
                <Button
                  onClick={() => startTrial(12)}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex flex-col items-center py-4"
                >
                  <Clock className="w-5 h-5 mb-1" />
                  <span className="font-bold">12h</span>
                </Button>
              </div>

              {/* Opção personalizada */}
              <div className="border-t border-zinc-700 pt-4">
                <Label className="text-slate-300 mb-2 block">Duração personalizada (hh:mm)</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Ex: 01:30"
                    value={customTrialTime}
                    onChange={(e) => {
                      // Auto-formatar: adicionar ":" após 2 dígitos
                      let val = e.target.value.replace(/[^0-9:]/g, '');
                      if (val.length === 2 && !val.includes(':') && customTrialTime.length < val.length) {
                        val = val + ':';
                      }
                      if (val.length > 5) val = val.slice(0, 5);
                      setCustomTrialTime(val);
                    }}
                    className="flex-1 text-center text-lg font-mono"
                    maxLength={5}
                  />
                  <Button
                    onClick={startTrialWithTime}
                    disabled={!customTrialTime}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-6"
                  >
                    Iniciar
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Mínimo: 00:01 (1 min) | Máximo: 72:00 (72h)</p>
              </div>

              <div className="bg-amber-900/20 border border-amber-800 rounded p-3 text-sm">
                <p className="text-amber-300">
                  ⚡ Crie um template do tipo &quot;Expiração de Teste&quot; para personalizar a mensagem de notificação.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Barra de Busca Fixa */}
        {!loading && clients.length > 0 && (
          <div className="sticky top-4 z-30 mb-4">
            <div className="relative">
              <svg 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <Input
                type="text"
                placeholder="Buscar por nome ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-slate-400 focus:border-violet-500 shadow-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filtros e Ordenação */}
        {!loading && clients.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-lg font-semibold text-white">Filtros</h3>
              <span className="text-sm text-slate-400">
                ({filteredClients.length} de {clients.length} clientes)
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filtro por Produto */}
              <div>
                <Label className="text-slate-300 mb-2 block">Filtrar por Produto</Label>
                <Select value={filterProduct} onValueChange={setFilterProduct}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os produtos</SelectItem>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Status */}
              <div>
                <Label className="text-slate-300 mb-2 block">Filtrar por Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="atrasado">Atrasado</SelectItem>
                    <SelectItem value="em_teste">🧪 Em Teste</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Toggle Arquivados */}
              <div>
                <Label className="text-slate-300 mb-2 block">Arquivados</Label>
                <Button
                  variant={showArchived ? "default" : "outline"}
                  onClick={() => setShowArchived(!showArchived)}
                  className={showArchived 
                    ? "w-full bg-slate-600 hover:bg-slate-700 text-white" 
                    : "w-full border-zinc-700 text-slate-400 hover:bg-zinc-800"
                  }
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {showArchived ? 'Ocultar Arquivados' : 'Ver Arquivados'}
                </Button>
              </div>

              {/* Ordenar por */}
              <div>
                <Label className="text-slate-300 mb-2 block">Ordenar por</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment_date">📅 Vencimento</SelectItem>
                    <SelectItem value="name">👤 Nome</SelectItem>
                    <SelectItem value="monthly_amount">💰 Valor</SelectItem>
                    <SelectItem value="product">📦 Produto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Ordem */}
              <div>
                <Label className="text-slate-300 mb-2 block">Ordem</Label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">
                      {sortBy === 'payment_date' ? '⬆️ Mais próximo' : '⬆️ Crescente (A-Z)'}
                    </SelectItem>
                    <SelectItem value="desc">
                      {sortBy === 'payment_date' ? '⬇️ Mais distante' : '⬇️ Decrescente (Z-A)'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Botão Limpar Filtros */}
            {(searchTerm || filterProduct !== 'all' || filterStatus !== 'all' || sortBy !== 'payment_date' || sortOrder !== 'asc') && (
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterProduct('all');
                    setFilterStatus('all');
                    setSortBy('payment_date');
                    setSortOrder('asc');
                  }}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-slate-300"
                >
                  Limpar Tudo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Seção de Clientes Arquivados - aparece logo após filtros */}
        {showArchived && (
          <div className="mb-6 bg-zinc-900/50 border border-zinc-700 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-400" />
              Clientes Arquivados ({archivedClients.length})
            </h2>
            
            {archivedClients.length === 0 ? (
              <p className="text-slate-400 text-center py-4">Nenhum cliente arquivado</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {archivedClients.map((client) => {
                  const product = products.find(p => p.id === client.product_id);
                  return (
                    <div
                      key={client.id}
                      className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-medium text-white">{client.name}</h3>
                          <p className="text-xs text-slate-400">{client.phone}</p>
                        </div>
                        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                          Arquivado
                        </span>
                      </div>
                      
                      {product && (
                        <p className="text-xs text-slate-400 mb-2">
                          {product.name} - R$ {product.price?.toFixed(2)}
                        </p>
                      )}
                      
                      {client.archived_at && (
                        <p className="text-xs text-slate-500 mb-2">
                          Em: {new Date(client.archived_at).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => restoreClient(client.id)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs py-1"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Restaurar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-500 border-rose-500/50 hover:bg-rose-600 hover:text-white text-xs py-1"
                          onClick={() => handleDelete(client.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Carregando...</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <p className="text-slate-400">Nenhum cliente cadastrado. Adicione seu primeiro cliente!</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <p className="text-slate-400 mb-2">Nenhum cliente encontrado com os filtros aplicados.</p>
            <Button
              onClick={() => {
                setFilterProduct('all');
                setFilterStatus('all');
              }}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Limpar Filtros
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => {
              const product = products.find(p => p.id === client.product_id);
              const group = groups.find(g => g.id === client.group_id);
              return (
                <div key={client.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-violet-500/30 transition-colors" data-testid={`client-card-${client.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">{client.name}</h3>
                      <p className="text-sm text-slate-400 mb-2">{client.phone}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={getStatusBadge(client.status)}>{getStatusLabel(client.status)}</span>
                        <WhatsappStatusPill status={waStatus.status} compact />
                        {group && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{
                            borderColor: `${group.color}40`,
                            backgroundColor: `${group.color}15`,
                            color: group.color
                          }}>
                            <div className="w-2 h-2 rounded-full" style={{backgroundColor: group.color}}></div>
                            {group.name}
                          </span>
                        )}
                      </div>
                      {/* Mostrar tempo restante do teste */}
                      {client.is_trial && client.trial_end && (
                        <div className="mt-2">
                          {(() => {
                            const remaining = getTrialTimeRemaining(client.trial_end);
                            return remaining && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                remaining.expired 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                {remaining.text}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => toggleNotifications(client.id, client.notifications_enabled !== false)}
                      className={client.notifications_enabled !== false ? 'text-emerald-500' : 'text-slate-500'}
                      title={client.notifications_enabled !== false ? 'Notificações ativas' : 'Notificações desativadas'}
                    >
                      {client.notifications_enabled !== false ? <Bell className="w-6 h-6" /> : <BellOff className="w-6 h-6" />}
                    </button>
                  </div>

                  <div className="space-y-2 mb-4 pb-4 border-b border-zinc-800">
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Vencimento:</span>
                        <span className="text-white">
                          {client.payment_date ? new Date(client.payment_date).toLocaleDateString('pt-BR') : '-'}
                        </span>
                      </div>
                      {client.payment_date && (
                        <div className="flex justify-end">
                          {getDueDateBadge(client.payment_date)}
                        </div>
                      )}
                    </div>
                    {product && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Plano:</span>
                        <span className="text-white">{product.name}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Valor:</span>
                      <span className="text-emerald-500 font-bold">R$ {client.monthly_amount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Campos Adicionais com Copy - Tela 1 */}
                  {(client.mac_email || client.otp_key || client.upload_link) && (
                    <div className="space-y-2 mb-3 pb-3 border-b border-zinc-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Monitor className="w-3.5 h-3.5 text-violet-400" />
                        <span className="text-xs font-medium text-violet-400">{client.screen_name || 'TV 1'}</span>
                      </div>
                      {client.mac_email && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">MAC:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono text-xs break-all">{client.mac_email}</span>
                            <button
                              onClick={() => copyToClipboard(client.mac_email, 'MAC/E-mail')}
                              className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                              title="Copiar MAC/E-mail"
                            >
                              <Copy className="w-3.5 h-3.5 text-violet-400" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {client.otp_key && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">OTP:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono text-xs break-all">{client.otp_key}</span>
                            <button
                              onClick={() => copyToClipboard(client.otp_key, 'Chave OTP')}
                              className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                              title="Copiar Chave OTP"
                            >
                              <Copy className="w-3.5 h-3.5 text-violet-400" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {client.upload_link && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">Link:</span>
                          <div className="flex items-center gap-2">
                            <a
                              href={client.upload_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-violet-400 hover:text-violet-300 flex items-center gap-1 text-xs"
                            >
                              Abrir
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Telas Adicionais */}
                  {client.screens && client.screens.length > 0 && client.screens.map((screen, screenIndex) => (
                    (screen.mac_email || screen.otp_key || screen.upload_link) ? (
                      <div key={screenIndex} className="space-y-2 mb-3 pb-3 border-b border-zinc-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Monitor className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-xs font-medium text-emerald-400">{screen.name || `TV ${screenIndex + 2}`}</span>
                        </div>
                        {screen.mac_email && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">MAC:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-mono text-xs break-all">{screen.mac_email}</span>
                              <button
                                onClick={() => copyToClipboard(screen.mac_email, `MAC ${screen.name || `TV ${screenIndex + 2}`}`)}
                                className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                                title={`Copiar MAC ${screen.name || `TV ${screenIndex + 2}`}`}
                              >
                                <Copy className="w-3.5 h-3.5 text-emerald-400" />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {screen.otp_key && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">OTP:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-mono text-xs break-all">{screen.otp_key}</span>
                              <button
                                onClick={() => copyToClipboard(screen.otp_key, `OTP ${screen.name || `TV ${screenIndex + 2}`}`)}
                                className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                                title={`Copiar OTP ${screen.name || `TV ${screenIndex + 2}`}`}
                              >
                                <Copy className="w-3.5 h-3.5 text-emerald-400" />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {screen.upload_link && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Link:</span>
                            <div className="flex items-center gap-2">
                              <a
                                href={screen.upload_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1 text-xs"
                              >
                                Abrir
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null
                  ))}

                  {/* Botões dos Links Vinculados */}
                  {(() => {
                    // Combinar link_id (legacy) com link_ids
                    const allLinkIds = [...(client.link_ids || [])];
                    if (client.link_id && !allLinkIds.includes(client.link_id)) {
                      allLinkIds.push(client.link_id);
                    }
                    
                    const linkedLinks = allLinkIds
                      .map(id => userLinks.find(l => l.id === id))
                      .filter(Boolean);
                    
                    if (linkedLinks.length === 0) return null;
                    
                    return (
                      <div className="mb-3 pb-3 border-b border-zinc-800">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-slate-400">Painéis vinculados:</p>
                          <button
                            type="button"
                            onClick={() => openLinkHistory(client)}
                            className="text-slate-400 hover:text-sky-400 transition-colors p-1 rounded"
                            title="Ver histórico de vínculos"
                            data-testid={`link-history-${client.id}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className={`grid gap-2 ${linkedLinks.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          {linkedLinks.map(linkedLink => (
                            <a
                              key={linkedLink.id}
                              href={linkedLink.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm"
                              data-testid={`client-link-${client.id}-${linkedLink.id}`}
                            >
                              {linkedLink.icon_url ? (
                                <img src={linkedLink.icon_url} alt="" className="w-4 h-4 rounded object-cover" />
                              ) : (
                                <Link2 className="w-4 h-4" />
                              )}
                              <span className="font-medium truncate">{linkedLink.name}</span>
                              <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      onClick={() => openWhatsApp(client.phone)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`whatsapp-${client.id}`}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openTemplateSender(client)}
                      className="bg-sky-600 hover:bg-sky-700 text-white"
                      data-testid={`send-template-${client.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Enviar
                    </Button>
                    
                    {/* Botões condicionais baseado no status */}
                    {client.is_trial ? (
                      <>
                        {/* Botões para cliente em teste */}
                        {client.product_id && (
                          <Button
                            size="sm"
                            onClick={() => openRenewDialog(client)}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`renew-${client.id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renovar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelTrial(client.id)}
                          className="border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
                          data-testid={`cancel-trial-${client.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        {/* Botões para cliente normal */}
                        {client.product_id && (
                          <Button
                            size="sm"
                            onClick={() => openRenewDialog(client)}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`renew-${client.id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renovar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => openTrialDialog(client)}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid={`trial-${client.id}`}
                        >
                          <FlaskConical className="w-4 h-4 mr-1" />
                          Teste
                        </Button>
                      </>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(client)}
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-slate-400 border-slate-600 hover:bg-slate-600 hover:text-white"
                      onClick={() => archiveClient(client.id)}
                      data-testid={`archive-client-${client.id}`}
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Arquivar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Dialog: Histórico de Vínculos */}
        <Dialog open={linkHistoryOpen} onOpenChange={setLinkHistoryOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg" data-testid="link-history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-sky-400" />
                Histórico de Vínculos
                {selectedClient && (
                  <span className="text-sm font-normal text-slate-400">— {selectedClient.name}</span>
                )}
              </DialogTitle>
            </DialogHeader>

            {linkHistoryLoading ? (
              <div className="py-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : linkHistoryData ? (
              <div className="space-y-4">
                {/* Atual */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Vinculado agora</p>
                  {linkHistoryData.current && linkHistoryData.current.length > 0 ? (
                    <div className="space-y-2">
                      {linkHistoryData.current.map((c) => (
                        <div key={c.link_id} className="flex items-start justify-between gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <div className="flex items-start gap-2 min-w-0">
                            <Link2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-emerald-300 truncate">{c.link_name || '(sem nome)'}</p>
                              <p className="text-xs text-slate-400 mt-0.5">Desde {formatDateTime(c.linked_at)}</p>
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-emerald-300 bg-emerald-500/20 px-2 py-1 rounded shrink-0">
                            {formatDays(c.days_active)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Nenhum painel vinculado no momento.</p>
                  )}
                </div>

                {/* Anterior */}
                {linkHistoryData.previous && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Último painel anterior</p>
                    <div className="p-3 bg-zinc-800/60 border border-zinc-700 rounded-lg">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 min-w-0">
                          <Link2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-200 truncate">{linkHistoryData.previous.link_name || '(sem nome)'}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {formatDateTime(linkHistoryData.previous.linked_at)} → {formatDateTime(linkHistoryData.previous.unlinked_at)}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-slate-300 bg-zinc-700 px-2 py-1 rounded shrink-0">
                          {formatDays(linkHistoryData.previous.duration_days)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Última troca */}
                {linkHistoryData.last_change_at && (
                  <div className="p-2.5 bg-sky-500/5 border border-sky-500/20 rounded-lg flex items-center gap-2">
                    <Clock className="w-4 h-4 text-sky-400 shrink-0" />
                    <p className="text-sm text-slate-300">
                      <span className="text-slate-400">Última troca:</span>{' '}
                      <span className="font-medium text-sky-300">{formatDateTime(linkHistoryData.last_change_at)}</span>
                    </p>
                  </div>
                )}

                {/* Histórico completo */}
                {linkHistoryData.history && linkHistoryData.history.length > 0 && (
                  <details className="group">
                    <summary className="cursor-pointer text-xs uppercase tracking-wider text-slate-500 font-semibold hover:text-slate-300 transition-colors">
                      Histórico completo ({linkHistoryData.history.length}) ▾
                    </summary>
                    <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {linkHistoryData.history.map((h, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 text-xs p-2 bg-zinc-800/40 rounded border border-zinc-800">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${h.active ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                            <span className="truncate text-slate-200">{h.link_name || '(sem nome)'}</span>
                          </div>
                          <span className="text-slate-400 shrink-0">
                            {h.active ? `desde ${formatDateTime(h.linked_at)}` : formatDays(h.duration_days)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 py-4 text-center">Sem dados de histórico.</p>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setLinkHistoryOpen(false)}
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800"
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
